import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import { truthy } from 'shared/lib'
import type { INormalizedApiError } from 'shared/api'
import modalStore from 'shared/ui/Modal/store/modalStore.ts'
import { ModalTypeList } from 'shared/ui/Modal/store/types.ts'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import {
  type IBaseSmartCallFlowData,
  type IFullSmartCallFlowData,
  type ISmartCallFlowStep,
} from 'entities/SmartCall/api/types'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import { showAutoRechargeRequired } from 'entities/Billing/actions/showAutoRechargeRequired.ts'
import { showUpgradePlanRequired } from 'entities/Billing/actions/showUpgradePlanRequired.ts'
import type { IFlowAction } from 'widgets/Flow'
import { DeleteParentActionStore } from 'widgets/Flow/actions/deleteParentAction/DeleteParentActionStore.ts'
import { DeleteSimpleActionStore } from 'widgets/Flow/actions/deleteSimpleAction/DeleteSimpleActionStore.tsx'
import { RingStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/Ring'
import { getSmartActionNextIdsFromStep } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/utils/getSmartActionNextIdsFromStep'
import { getSmartCallActionTypeFromStep } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/utils/getSmartCallActionTypeFromStep'
import { ForwardCallStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/ForwardCall'
import { isHaveParentCallEnds } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/utils/isHaveParentCallEnds.ts'
import { getAllChildActionsById } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/utils/getAllChildActionsById.ts'
import { hasCallActions } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/utils/hasCallActions.ts'
import {
  AICallingAgentStore,
  AITextingAgentStore,
  BusinessHoursStore,
  CallEndsStore,
  CallQueueStore,
  IncomingCallStore,
  IVRStore,
  PlayAudioStore,
  SendSMSStore,
  SendToVoicemailStore,
} from '../ui/actions'
import { type ISmartCallFlowAction, type ISmartCallFlowActionType } from '../ui/actions/type'
import { AddButtonStore } from '../ui/actions/AddButton/store/AddButtonStore'
import { type ISmartCallActionStore } from '../ui/Action'

export class SmartCallFlowBuilderStore {
  constructor(
    public readonly entities: IEntitiesStore,
    readonly parent: { inbox: Inbox | null },
    public response?: IFullSmartCallFlowData
  ) {
    this._init(this.response)
    makeAutoObservable(this)
  }

  init = () => {
    this.reactionActionIds()
  }
  dispose = () => {
    this._disposeActionIds?.()
  }

  get params(): IBaseSmartCallFlowData | null {
    if (!this.trigger) return null
    return {
      name: this.name,
      description: '',
      definition: {
        trigger: this.trigger.step,
        steps: this.steps,
      },
    }
  }

  name = ''
  template: string | null = null

  get trigger() {
    const trigger = this.actions[0]
    if (trigger instanceof IncomingCallStore) {
      return trigger
    }
  }

  get steps(): IBaseSmartCallFlowData['definition']['steps'] {
    const addButtonIds = new Set(
      this.actions.filter((a) => a instanceof AddButtonStore).map((a) => a.actionStore.id)
    )
    const steps: IBaseSmartCallFlowData['definition']['steps'] = {}
    this.actions.slice(1).forEach((action) => {
      if (action instanceof AddButtonStore) return
      const step = action.step
      if (step.next !== undefined && addButtonIds.has(step.next) && step.id !== 'inboundCall') {
        steps[action.actionStore.id] = { ...step, next: undefined }
      } else {
        steps[action.actionStore.id] = step
      }
    })
    return steps
  }

  private _actionsMap: Map<string, ISmartCallFlowAction> = new Map()
  addAction = (action: ISmartCallFlowAction) => {
    this._actionsMap.set(action.actionStore.id, action)
  }

  get actions() {
    return Array.from(this._actionsMap.values())
  }

  private get _actionsIds() {
    return Array.from(this._actionsMap.keys())
  }

  getActionById = (id: string) => {
    return this._actionsMap.get(id)
  }

  createAction = (
    actionType: ISmartCallFlowActionType,
    {
      parentId,
      id,
      childId,
      step,
    }: {
      parentId: string
      childId?: string
      id?: string
      step?: ISmartCallFlowStep
    }
  ) => {
    const commonProps = { parentId, id, childId }

    switch (actionType) {
      case 'incomingCall': {
        return new IncomingCallStore()
      }
      case 'callQueue': {
        const args = (step?.id === 'callQueue' && step.args) || undefined
        return new CallQueueStore(this.entities, { ...commonProps, args }, this.parent)
      }
      case 'callingFlow': {
        const args = (step?.id === 'callingFlow' && step.args) || undefined
        return new RingStore(this.entities, { ...commonProps, args }, this.parent)
      }
      case 'businessHours': {
        const args = (step?.id === 'businessHours' && step.args) || undefined
        return new BusinessHoursStore(this.entities, { ...commonProps, args }, this)
      }
      case 'sendVoicemail': {
        const args = (step?.id === 'sendVoicemail' && step.args) || undefined
        return new SendToVoicemailStore(this.entities, { ...commonProps, args }, this)
      }
      case 'playSound': {
        const args = (step?.id === 'playSound' && step.args) || undefined
        return new PlayAudioStore(this.entities, { ...commonProps, args }, this.parent)
      }
      case 'forwardCall': {
        const args = (step?.id === 'forwardCallMulti' && step.args) || undefined
        return new ForwardCallStore(this.entities, { ...commonProps, args }, this)
      }
      case 'sendMessage': {
        const args = (step?.id === 'sendMessageSync' && step.args) || undefined
        return new SendSMSStore(this.entities, {
          ...commonProps,
          args,
          defaultInboxId: this.parent.inbox?.id,
        })
      }
      case 'ivrPhoneTree': {
        const args = (step?.id === 'ivrPhoneTree' && step.args) || undefined
        return new IVRStore(this.entities, { ...commonProps, args }, this)
      }
      case 'callEnds': {
        return new CallEndsStore({ ...commonProps })
      }
      case 'aiTextingAgent': {
        const args = (step?.id === 'aiAgentSync' && step.args) || undefined
        return new AITextingAgentStore(this.entities, { ...commonProps, args })
      }
      case 'aiCallingAgent': {
        const args = (step?.id === 'aiCallingAgent' && step.args) || undefined
        return new AICallingAgentStore(this.entities, { ...commonProps, args })
      }
      case 'addButton': {
        return new AddButtonStore({ id, parentId })
      }

      default: {
        return null
      }
    }
  }

  addActionByType = async (
    actionType: ISmartCallFlowActionType,
    source: string,
    target: string
  ) => {
    const sourceAction = this.getActionById(source)?.actionStore
    const targetFlowAction = this.getActionById(target)
    let targetAction = targetFlowAction?.actionStore

    const newWorkflowAction: ISmartCallFlowAction | null = this.createAction(actionType, {
      parentId: source,
      childId: target,
    })

    if (!newWorkflowAction) return
    if (!targetFlowAction) return

    const checkBeforeCreate = async (): Promise<boolean> => {
      if (actionType === 'forwardCall') {
        if (this.entities.subscriptionStore.isTrial || this.entities.subscriptionStore.isPAYGPlan) {
          return new Promise((resolve) => {
            showUpgradePlanRequired(this.entities, {
              onFailed: () => resolve(false),
              useFor: 'call forwarding',
            })
          })
        }
        if (!this.entities.billingStore.autorecharge?.is_active) {
          return new Promise((resolve) => {
            showAutoRechargeRequired(this.entities, {
              onSuccess: () => resolve(true),
              onFailed: () => resolve(false),
              useFor: 'call forwarding',
            })
          })
        }
        return true
      }

      const isNeedDeleteAllAfterActions = () => {
        if (targetFlowAction instanceof CallEndsStore) return false
        if (targetFlowAction instanceof AddButtonStore) return false

        const childActions = getAllChildActionsById({
          id: target,
          getActionById: this.getActionById,
        })

        const checkAllowChildActions = (allowedAction: ISmartCallFlowActionType[]) => {
          return childActions.some((action) => !allowedAction.includes(action.actionStore.type))
        }

        if (newWorkflowAction instanceof RingStore) {
          return childActions.some(
            (action) =>
              action.actionStore.type === 'businessHours' ||
              action.actionStore.type === 'ivrPhoneTree'
          )
        }
        if (newWorkflowAction instanceof CallQueueStore) {
          return checkAllowChildActions([
            'callEnds',
            'aiTextingAgent',
            'addButton',
            'sendMessage',
            'sendVoicemail',
          ])
        }
        if (
          newWorkflowAction instanceof SendToVoicemailStore ||
          newWorkflowAction instanceof AICallingAgentStore
        ) {
          return checkAllowChildActions(['callEnds', 'aiTextingAgent', 'addButton', 'sendMessage'])
        }
        return false
      }

      if (isNeedDeleteAllAfterActions()) {
        return new Promise((resolve) => {
          const modalId = nanoid()

          modalStore.addModal({
            id: modalId,
            type: ModalTypeList.WARNING,
            title: 'Delete everything after this action?',
            desc: (
              <>
                Adding “{newWorkflowAction.actionStore.name}” here will remove all following actions
              </>
            ),
            primaryAction: {
              text: 'Yes, delete',
              onAction: () => {
                this.removeActionAndAllChildren(targetFlowAction)
                this.appendCallEndsWithAddButton(newWorkflowAction.actionStore, target)

                resolve(true)
                targetAction = undefined

                modalStore.removeModal(modalId)
              },
            },
            closeActionButtonProps: {
              text: 'Cancel',
              contained: 'secondary',
            },
            onClose: () => {
              resolve(false)
              modalStore.removeModal(modalId)
            },
          })
        })
      }
      return true
    }

    const isAccess = await checkBeforeCreate()
    if (!isAccess) return

    const noSelectionSendToVoicemailAction = this.createAction('sendVoicemail', {
      parentId: newWorkflowAction.actionStore.id,
    })

    const addRingAction = () => {
      if (targetFlowAction instanceof CallEndsStore) {
        // add ring action for first branch
        const ringAction = this.createAction('callingFlow', {
          parentId: newWorkflowAction.actionStore.id,
          childId: target,
        })
        if (ringAction) {
          newWorkflowAction.actionStore.replaceNextId(target, ringAction.actionStore.id)
          this.addAction(ringAction)

          const sendToVoicemailAction: ISmartCallFlowAction | null = this.createAction(
            'sendVoicemail',
            {
              parentId: ringAction.actionStore.id,
            }
          )
          if (sendToVoicemailAction) {
            ringAction.actionStore.addNextId(sendToVoicemailAction.actionStore.id)
            this.addAction(sendToVoicemailAction)
            this.appendCallEndsWithAddButton(sendToVoicemailAction.actionStore)
          }
        }
      }
    }

    const addDefaultChildAction = () => {
      sourceAction?.replaceNextId(target, newWorkflowAction.actionStore.id)
      targetAction?.setParentId(newWorkflowAction.actionStore.id)
      this.addAction(newWorkflowAction)
      this.setSelectedActionStore(newWorkflowAction)
    }

    if (newWorkflowAction instanceof IVRStore) {
      addDefaultChildAction()

      if (targetFlowAction instanceof CallEndsStore) {
        newWorkflowAction.addBranch(target)

        if (noSelectionSendToVoicemailAction) {
          newWorkflowAction.actionStore.replaceNextId(
            target,
            noSelectionSendToVoicemailAction.actionStore.id
          )
        }
      } else {
        if (noSelectionSendToVoicemailAction) {
          newWorkflowAction.actionStore.addNextId(noSelectionSendToVoicemailAction.actionStore.id)
        }

        newWorkflowAction.addBranchData({
          key: 1,
          name: '',
        })
      }

      if (noSelectionSendToVoicemailAction) {
        this.addAction(noSelectionSendToVoicemailAction)
        this.appendCallEndsWithAddButton(noSelectionSendToVoicemailAction.actionStore)
      }
      return
    }

    if (newWorkflowAction instanceof RingStore) {
      addDefaultChildAction()

      if (targetFlowAction instanceof CallEndsStore || !targetAction) {
        if (noSelectionSendToVoicemailAction) {
          newWorkflowAction.actionStore.addNextId(noSelectionSendToVoicemailAction.actionStore.id)
          this.addAction(noSelectionSendToVoicemailAction)
          this.appendCallEndsWithAddButton(noSelectionSendToVoicemailAction.actionStore)
        }
      } else {
        newWorkflowAction.actionStore.removeNextId(target)
        this.appendCallEndsWithAddButton(newWorkflowAction.actionStore)
        newWorkflowAction.actionStore.addNextId(target)
      }
      return
    }

    if (newWorkflowAction instanceof ForwardCallStore) {
      addDefaultChildAction()
      addRingAction()

      const callEnds = this.createAction('callEnds', {
        parentId: newWorkflowAction.actionStore.id,
      })

      if (callEnds) {
        this.addAction(callEnds)
        this._appendAddButton(callEnds?.actionStore)
        newWorkflowAction.actionStore.addNextIdBefore(callEnds.actionStore?.id)
      }
      return
    }

    if (newWorkflowAction instanceof BusinessHoursStore) {
      addDefaultChildAction()
      addRingAction()

      if (noSelectionSendToVoicemailAction) {
        newWorkflowAction.actionStore.addNextId(noSelectionSendToVoicemailAction.actionStore.id)
        this.addAction(noSelectionSendToVoicemailAction)
        this.appendCallEndsWithAddButton(noSelectionSendToVoicemailAction.actionStore)
      }
      return
    }

    addDefaultChildAction()
  }

  private _selectedActionStoreId: string | null = null

  get selectedActionStore() {
    return (
      (this._selectedActionStoreId && this._actionsMap.get(this._selectedActionStoreId)) || null
    )
  }

  setSelectedActionStore = (selectedActionStore: ISmartCallFlowAction | null) => {
    if (selectedActionStore?.actionStore.id === this._selectedActionStoreId) return
    this._selectedActionStoreId = selectedActionStore?.actionStore.id || null
  }
  closeSelectedAction = () => {
    this._selectedActionStoreId = null
  }

  private _appendCallEnds = (
    parentActionStore: ISmartCallActionStore,
    replaceId?: string
  ): CallEndsStore => {
    const end = new CallEndsStore({ parentId: parentActionStore.id })
    if (replaceId !== undefined) {
      parentActionStore.replaceNextId(replaceId, end.actionStore.id)
    } else {
      parentActionStore.addNextId(end.actionStore.id)
    }
    this.addAction(end)
    return end
  }

  appendCallEndsWithAddButton = (parentActionStore: ISmartCallActionStore, replaceId?: string) => {
    this._appendAddButton(this._appendCallEnds(parentActionStore, replaceId).actionStore)
  }

  private _appendAddButton = (parentActionStore: ISmartCallActionStore, replaceId?: string) => {
    const addButton = new AddButtonStore({ parentId: parentActionStore.id })
    if (replaceId !== undefined) {
      parentActionStore.replaceNextId(replaceId, addButton.actionStore.id)
    } else {
      parentActionStore.addNextId(addButton.actionStore.id)
    }
    this.addAction(addButton)
  }

  removeActionAndAllChildren = (action: IFlowAction) => {
    this._actionsMap.delete(action.actionStore.id)
    action.actionStore.nextIds.forEach((id: string) => {
      const actionNew = this._actionsMap.get(id)
      if (actionNew) {
        return this.removeActionAndAllChildren(actionNew)
      }
    })
  }

  checkHasCallActionSteps = (): boolean => {
    if (!this.trigger) return false
    if (!this.hasCallActionsById(this.trigger?.actionStore.id)) {
      return false
    }

    let valid = true

    this.actions.forEach((action) => {
      if ('checkHasCallActionSteps' in action && !action.checkHasCallActionSteps()) {
        valid = false
      }
    })

    return valid
  }

  validateForPublish = (): boolean => {
    if (!this.checkHasCallActionSteps()) return false
    return this.actions
      .map((action) => ('validate' in action ? action.validate() : true))
      .every(Boolean)
  }

  setError = (error: INormalizedApiError) => {
    error.entries().forEach(([key, apiError]) => {
      const value = apiError.value
      const id = key.split('.')[0]

      if (id) {
        const action = this.getActionById(id)
        if (action && 'setApiError' in action) {
          action?.setApiError(key, value)
        }
      }
    })
  }

  buildDefaultWorkflow = () => {
    this._actionsMap.clear()

    const trigger = new IncomingCallStore()
    this.addAction(trigger)

    const ringAction = new RingStore(
      this.entities,
      {
        parentId: trigger.actionStore.id,
        args: {
          ring_type: 'simultaneous',
          user_ids: [],
          duration: 30,
          crm_property: null,
          crm_key: null,
          auto_10_members: true,
          next_on_answered: '',
          next_on_no_answer: '',
        },
      },
      this.parent
    )

    trigger.actionStore.addNextId(ringAction.actionStore.id)
    this.addAction(ringAction)

    this.appendCallEndsWithAddButton(ringAction.actionStore)
    this.appendCallEndsWithAddButton(ringAction.actionStore)
  }

  setName = (name: string) => {
    this.name = `${name} Call Flow`
  }

  loadFromWorkflow = (response: IFullSmartCallFlowData) => {
    this._actionsMap.clear()
    this._init(response)
  }

  private _init = (response?: IFullSmartCallFlowData): void => {
    if (response) {
      const definition = response.definition
      const trigger = new IncomingCallStore()
      this.addAction(trigger)

      const innerFirstStepId = definition.trigger.next

      const createAction = (
        newStepId: string,
        parentWorkflowAction: ISmartCallFlowAction,
        map: Record<string, ISmartCallFlowStep>
      ) => {
        const newStep = map[newStepId]
        if (!newStep) return

        const nextIds = getSmartActionNextIdsFromStep(newStep)
        const actionType = getSmartCallActionTypeFromStep(newStep)

        if (!actionType) return

        const newWorkflowAction = this.createAction(actionType, {
          id: newStepId,
          parentId: parentWorkflowAction.actionStore.id,
          step: newStep,
        })
        if (!newWorkflowAction) {
          throw new Error('No action found')
        }
        parentWorkflowAction.actionStore.addNextId(newWorkflowAction.actionStore.id)
        this.addAction(newWorkflowAction)

        nextIds.forEach((nextId) => {
          if (nextId) createAction(nextId, newWorkflowAction, map)
        })

        if (
          newWorkflowAction instanceof RingStore &&
          newWorkflowAction.actionStore.nextIds.length === 1
        ) {
          const noAnswerId = newWorkflowAction.actionStore.nextIds[0]
          newWorkflowAction.actionStore.removeNextId(noAnswerId)
          this._appendCallEnds(newWorkflowAction.actionStore)
          newWorkflowAction.actionStore.addNextId(noAnswerId)
        }
      }

      try {
        createAction(innerFirstStepId, trigger, definition.steps)
      } catch (e) {
        this.buildDefaultWorkflow()
      }

      this.actions.forEach((action) => {
        if (
          (action instanceof CallEndsStore || action instanceof SendSMSStore) &&
          action.actionStore.nextIds.length === 0
        ) {
          this._appendAddButton(action.actionStore)
        }
      })
    }
  }

  hasCallActionsById = (id: string): boolean => hasCallActions(id, this.getActionById)

  deleteAction = (store: IFlowAction) => {
    const isLastAction = () => {
      if (store.actionStore.nextIds.length > 1) return false

      const nextAction = this.getActionById(store.actionStore.nextIds[0])
      const nextNextAction =
        nextAction &&
        nextAction.actionStore.nextIds.length === 1 &&
        this.getActionById(nextAction?.actionStore.nextIds[0])
      if (
        nextAction instanceof AddButtonStore ||
        (nextAction instanceof CallEndsStore && nextNextAction instanceof AddButtonStore)
      ) {
        return true
      }
    }
    if (isLastAction()) {
      this._deleteAction(store, false)
      return
    }
    if (store.actionStore.nextIds.length > 1) {
      new DeleteParentActionStore(() => this._deleteAction(store, true))
    } else {
      new DeleteSimpleActionStore((isNextDelete) => {
        this._deleteAction(store, isNextDelete)
      })
    }
  }

  private _deleteAction = (workFlowAction: IFlowAction, isNextDelete: boolean) => {
    const action = workFlowAction.actionStore

    const parent = this.getActionById(action.parentId)?.actionStore
    const children = action.nextIds
      .map(this.getActionById)
      .map((action) => action?.actionStore)
      .filter(truthy)

    if (!parent) return
    if (isNextDelete) {
      if (isHaveParentCallEnds(workFlowAction, this.getActionById)) {
        parent.removeNextId(action.id)
        this.removeActionAndAllChildren(workFlowAction)
        this._appendAddButton(parent)
      } else {
        this.removeActionAndAllChildren(workFlowAction)
        this.appendCallEndsWithAddButton(parent, action.id)
      }
    } else {
      if (children.length === 1) {
        parent.replaceNextId(action.id, children[0].id)
        children[0].setParentId(parent.id)
      } else {
        parent.removeNextId(action.id)
        children.forEach((child) => {
          parent.addNextId(child.id)
          child.setParentId(parent.id)
        })
      }

      this._actionsMap.delete(action.id)
    }
  }

  private _disposeActionIds: IReactionDisposer | null = null
  reactionActionIds = () => {
    this._disposeActionIds?.()
    this._disposeActionIds = reaction(
      () => [this._actionsIds, this._selectedActionStoreId],
      () => {
        if (this._selectedActionStoreId && !this._actionsMap.has(this._selectedActionStoreId)) {
          this.closeSelectedAction()
        }
      }
    )
  }

  get isAutoRechargeRequired() {
    return !!this.actions.find(
      (action) => 'isAutoRechargeRequired' in action && action.isAutoRechargeRequired
    )
  }
}
